import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Interfaces para Power BI
 */
export interface EmbedConfig {
    embedToken: string;
    embedUrl: string;
    reportId: string;
    expiration: string;
}

export interface ReportInfo {
    id: string;
    name: string;
    embedUrl: string;
    datasetId: string;
}

export interface PowerBIStatus {
    configured: boolean;
    message: string;
}

export interface PowerBIStatusResponse {
    powerbi: PowerBIStatus;
    reporteria: PowerBIStatus;
}

export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
    /** Código de error estable para soporte (ej. OPB-PBI-001) */
    errorCode?: string;
    timestamp: string;
}

export interface EmbedTokenRequest {
    reportId: string;
    username?: string;
    roles?: string[];
}

/**
 * Servicio para interactuar con la API de Power BI
 * Proporciona métodos para obtener configuración de embed y tokens
 */
@Injectable({
    providedIn: 'root',
})
export class PowerBIService {
    private apiUrl = `${environment.apiUrl}/v1/powerbi`;

    constructor(private http: HttpClient) {}

    /**
     * Verifica el estado del servicio Power BI
     */
    getStatus(): Observable<ApiResponse<PowerBIStatusResponse>> {
        return this.http.get<ApiResponse<PowerBIStatusResponse>>(
            `${this.apiUrl}/status`,
            { headers: this.getAuthHeaders() }
        );
    }

    /**
     * Obtiene la lista de reportes disponibles
     * @param category Filtrar por categoría: operativo, financiero, gerencial
     */
    getReports(category?: string): Observable<ApiResponse<ReportInfo[]>> {
        let url = `${this.apiUrl}/reports`;
        if (category) {
            url += `?category=${encodeURIComponent(category)}`;
        }
        return this.http.get<ApiResponse<ReportInfo[]>>(url, {
            headers: this.getAuthHeaders(),
        });
    }

    /**
     * Obtiene todos los reportes sin filtro de rol (admin only)
     */
    getAllReports(): Observable<ApiResponse<ReportInfo[]>> {
        return this.http.get<ApiResponse<ReportInfo[]>>(
            `${this.apiUrl}/all-reports`,
            { headers: this.getAuthHeaders() }
        );
    }

    /**
     * Obtiene la configuración de embed para un reporte
     * Sin Row Level Security
     */
    getEmbedConfig(reportId: string): Observable<ApiResponse<EmbedConfig>> {
        return this.http.get<ApiResponse<EmbedConfig>>(
            `${this.apiUrl}/embed-config/${reportId}`,
            { headers: this.getAuthHeaders() }
        );
    }

    /**
     * Genera un token de embed con Row Level Security
     */
    generateEmbedToken(
        request: EmbedTokenRequest
    ): Observable<ApiResponse<EmbedConfig>> {
        return this.http.post<ApiResponse<EmbedConfig>>(
            `${this.apiUrl}/embed-token`,
            request,
            { headers: this.getAuthHeaders() }
        );
    }

    /**
     * Obtiene el token de autenticación del localStorage
     */
    private getAuthToken(): string | null {
        return localStorage.getItem('access_token');
    }

    /**
     * Crea headers con token de autenticación
     */
    private getAuthHeaders(): HttpHeaders {
        const token = this.getAuthToken();
        return new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        });
    }
}
